import {Component, OnInit, inject} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, } from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {
    ConfiguratorDataViewService,
    ConfiguratorSaveQueryService,
    ConfiguratorSendInBlueService,
    ConfiguratorTemplateHtmlService,
    ConfiguratorTransactionalEmailService,
    KeyValueOflongAndstring,
    KeyValueStringString,
    SelectItem,
    TemplateHtmlDTO,
    TransactionalEmailConfDTO
} from '@tj/tj-dmp-lib-api';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {resolveI18nLabel} from '../../../../pipes/i18n-label.pipe';
import {LangService} from '../../../../services/lang.service';

type TemplateSendingblue = KeyValueOflongAndstring;

@Component({
    templateUrl: './transactional-edit.component.html',
    styleUrls: ['./transactional-edit.component.scss'],
    standalone: false
})
export class TransactionalEditComponent implements OnInit {
    private readonly langService = inject(LangService);
    breadcrumb = [
        { label: this.translate.instant('menu.marketing.label') },
        { label: this.translate.instant('menu.workflow.marketing-emails') },
        { label: this.translate.instant('campaign.edit.page.breadcrumbCreate') },
    ];
    description = this.translate.instant('marketingEmails.create-edit.pageDescription.create');

    steps = [
        { id: 1, label: this.translate.instant('marketingEmails.create-edit.step-title.settings') },
        { id: 2, label: this.translate.instant('marketingEmails.create-edit.step-title.customization') },
        { id: 3, label: this.translate.instant('marketingEmails.create-edit.step-title.block-editor') },
    ];
    currentStep = 1;

    loaded = false;

    actual: TransactionalEmailConfDTO;

    selectedTemplates: TemplateHtmlDTO[] = [];
    templates: TemplateHtmlDTO[] = [];
    cron = '';
    dataViews: SelectItem[] = [];
    langs = ['FR', 'EN', 'IT', 'DE', 'NL', 'ES'] as const;
    brevoTemplates: TemplateSendingblue[];
    brevoTemplate: number;
    senderEmails: SelectItem[] = [];
    form: UntypedFormGroup;
    submitted = false;
    translations = [];

    emailTypes: SelectItem[] = [
        { label: this.translate.instant('marketingEmails.create-edit.label.marketing'), value: TransactionalEmailConfDTO.TypeEnum.MARKETING },
        { label: this.translate.instant('marketingEmails.create-edit.label.transactional'), value: TransactionalEmailConfDTO.TypeEnum.TRANSACTIONAL }
    ];

    sendToOptions: SelectItem[] = [
        {
            label: this.translate.instant('marketingEmails.create-edit.label.sendToFullname'),
            value: 'nameAndSurname',
        },
        {
            label: this.translate.instant('marketingEmails.create-edit.label.sendToName'),
            value: 'nameOnly',
        },
        {
            label: this.translate.instant('marketingEmails.create-edit.label.email'),
            value: 'emailAddress',
        }
    ];

    advPanelOpen = false;

    constructor(
        private route: ActivatedRoute,
        private emailService: ConfiguratorTransactionalEmailService,
        private fb: UntypedFormBuilder,
        protected templateHtmlService: ConfiguratorTemplateHtmlService,
        private messageService: MessageService,
        private readonly configuratorDataViewService: ConfiguratorDataViewService,
        private router: Router,
        private sendingblueService: ConfiguratorSendInBlueService,
        private configuratorSaveQueryService: ConfiguratorSaveQueryService,
        private readonly translate: TranslateService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private isEditMode = false;

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            { label: this.translate.instant('menu.marketing.label') },
            { label: this.translate.instant('menu.workflow.marketing-emails') },
            { label: this.translate.instant(this.isEditMode ? 'campaign.group.page.breadcrumbEdit' : 'campaign.edit.page.breadcrumbCreate') },
        ];
        this.description = this.translate.instant(this.isEditMode
            ? 'marketingEmails.create-edit.pageDescription.edit'
            : 'marketingEmails.create-edit.pageDescription.create');
    }

    tenant: string;
    partner: string;

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    get currentCode() {
        return (
            'TEMAIL_' +
            ((this.form.value.name as string) ?? '')
                .toUpperCase()
                .replace(/ /, '-')
        );
    }

    ngOnInit() {
        this.description = this.translate.instant('marketingEmails.create-edit.pageDescription.create');
        this.tenant = this.route.snapshot.paramMap.get('tenant');
        this.partner = this.route.snapshot.paramMap.get('partner');
        this.form = this.fb.group({
            tenant: [this.tenant, Validators.required],
            partner: [this.partner, Validators.required],
            type: [this.emailTypes[0].value, Validators.required],
            enabled: [true, Validators.required],
            name: [null, Validators.required],
            description: [null],
            brevoTemplate: [null, Validators.required],
            codeDataView: [null, Validators.required],
            lang: ['FR', Validators.required],
            senderEmail: [null, Validators.required],
            senderName: [null, Validators.required],
            replyToEmail: [null],
            sendTo: [this.sendToOptions[0].value],
            subject: [null, Validators.required],
            previewText: [null],
            marketingTag: [null],
            linkShowNav: [false],
            utmSource: [null],
            utmMedium: [null],
            utmCampaign: [null],
            utmContent: [null]
        });
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.loaded = true;
        }
        this.templateHtmlService
            .getAllTemplateHtmlsUsingGET(this.tenant)
            .subscribe((res) => {
                this.templates = res;
            });
        this.updateDataViews()
            .then(() => this.listingbrevoTemplate())
            .then(() => this.loadSenderEmails())
            .then(() => this.initOnUpdate());
    }

    initOnUpdate() {
        const id = this.route.snapshot.paramMap.get('id');

        if (id) {
            this.isEditMode = true;
            this.rebuildTranslatedHeader();
            this.emailService
                .getTransactionalEmailUsingGET(this.tenant, id)
                .subscribe((res) => {
                    this.actual = res;
                    this.selectedTemplates = this.templates.filter(
                        (el) =>
                            this.actual.codesTemplateHtml.indexOf(el.code) >= 0
                    );
                    this.templates = this.templates.filter(
                        (el) =>
                            this.actual.codesTemplateHtml.indexOf(el.code) < 0
                    );
                    this.cron = res.cron;
                    const dataView = this.dataViews.find(
                        (el) => el.value === this.actual.codeDataView
                    )?.value;
                    this.translations = this.actual.dictionary;
                    this.brevoTemplate = this.actual.templateId
                        ? this.brevoTemplates.find(
                              (el) => el.key === this.actual.templateId
                          )?.key
                        : this.brevoTemplates[0]?.key;
                    this.form = this.fb.group({
                        name: [this.actual.name, Validators.required],
                        type: [this.actual.type, Validators.required],
                        description: [this.actual.description],
                        enabled: [this.actual.enable, Validators.required],
                        brevoTemplate: [ this.brevoTemplate, Validators.required ],
                        lang: [ this.actual.defaultLanguage, Validators.required ],
                        codeDataView: [ dataView, Validators.required ],
                        partner: [this.actual.partner, Validators.required],
                        tenant: [this.actual.tenant, Validators.required],
                        senderEmail: [this.actual.senderEmail, Validators.required],
                        senderName: [this.actual.senderName, Validators.required],
                        replyToEmail: [this.actual.replyToEmail],
                        sendTo: [this.actual.sendTo ?? this.sendToOptions[0].value],
                        subject: [this.actual.subject, Validators.required],
                        previewText: [this.actual.previewText],
                        marketingTag: [this.actual.marketingTag],
                        linkShowNav: [this.actual.linkShowNav ?? false],
                        utmSource: [this.actual.utmSource],
                        utmMedium: [this.actual.utmMedium],
                        utmCampaign: [this.actual.utmCampaign],
                        utmContent: [this.actual.utmContent]
                    });
                    this.loaded = true;
                });
        } else {
            this.loaded = true;
        }
    }

    goToStep(stepId: number) {
        this.currentStep = stepId;
    }

    nextStep() {
        if (this.currentStep < this.steps.length) {
            this.currentStep++;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    previousStep() {
        if (this.currentStep > 1) {
            this.currentStep--;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    submit() {
        this.save(
            () => {
                const navigationExtras: NavigationExtras = {
                    state: { message: 'success_created' },
                };
                this.router.navigate(
                    [`/h/marketing/marketing-email`],
                    navigationExtras
                );
            },
            () => {
                const navigationExtras: NavigationExtras =
                    !this.route.snapshot.paramMap.get('id')
                        ? { state: { message: 'success_created' } }
                        : { state: { message: 'success_updated' } };
                // const navigationExtras: NavigationExtras = {state: {message: 'success_updated'}};
                this.router.navigate(
                    [`/h/marketing/marketing-email`],
                    navigationExtras
                );
            }
        );
    }
    save(funOnCreate: () => void, funOnUpdate: () => void) {
        this.submitted = true;
        const status = this.form.status;
        if (status === 'INVALID') {
            this.messageService.add({
                key: 'component',
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('marketingEmails.create-edit.messages.invalidFormDetail'),
            });
            return;
        }
        const value = this.form.value;
        const email: TransactionalEmailConfDTO = {
            code: this.currentCode,
            name: value.name,
            description: value.description,
            templateId: value.brevoTemplate,
            provider: null,
            type: value.type,
            enable: value.enabled,
            idSaveQuery: null,
            codeDataView: value.codeDataView,
            codesTemplateHtml: this.selectedTemplates.map((el) => el.code),
            tenant: this.tenant,
            partner: value.partner,
            cron: this.cron,
            defaultLanguage: value.lang,
            dictionary: this.translations,
            etl: null,
            triggerCodeEtl: null,
            senderEmail: value.senderEmail,
            senderName: value.senderName,
            replyToEmail: value.replyToEmail,
            sendTo: value.sendTo,
            subject: value.subject,
            previewText: value.previewText,
            marketingTag: value.marketingTag,
            linkShowNav: value.linkShowNav,
            utmSource: value.utmSource,
            utmMedium: value.utmMedium,
            utmCampaign: value.utmCampaign,
            utmContent: value.utmContent
        };
        this.updatebrevoTemplate(value.brevoTemplate);

        if (!this.actual) {
            this.emailService
                .createTransactionalEmailUsingPOST(this.tenant, email)
                .subscribe(
                    (res) => {
                        this.actual = res;
                        funOnCreate();
                        // const navigationExtras: NavigationExtras = {state: {message: 'success_created'}};
                        // this.router.navigate([`/h/marketing/transactional-email`], navigationExtras);
                    },
                    (e) => {
                        this.messageService.add({
                            key: 'component',
                            severity: 'error',
                            summary: this.translate.instant('datastore.errorSummary'),
                            detail: this.translate.instant('marketingEmails.create-edit.messages.errorOccurredDetail', {error: e.toString()}),
                        });
                    }
                );
        } else {
            email.id = this.actual.id;
            this.emailService
                .updateTransactionalEmailUsingPUT(this.tenant, email)
                .subscribe(
                    () => {
                        funOnUpdate();
                        // const navigationExtras: NavigationExtras = {state: {message: 'success_updated'}};
                        // this.router.navigate([`/h/marketing/transactional-email`], navigationExtras);
                    },
                    (e) => {
                        this.messageService.add({
                            key: 'component',
                            severity: 'error',
                            summary: this.translate.instant('datastore.errorSummary'),
                            detail: this.translate.instant('marketingEmails.create-edit.messages.errorOccurredDetail', {error: e.toString()}),
                        });
                    }
                );
        }
    }

    private updateDataViews() {
        if (this.tenant) {
            return new Promise<void>((resolve) =>
                this.configuratorDataViewService
                    .getAllDataViewsUsingGET(this.tenant)
                    .subscribe((data) => {
                        this.dataViews = data
                            // .filter((d) => d.code.includes(this.groupDataView))
                            .sort((a, b) => a.code.localeCompare(b.code))
                            .map((el) => ({
                                label: resolveI18nLabel(el.labels, this.langService.currentLang, el.name),
                                value: el.code,
                            }));
                        resolve();
                    })
            );
        }
    }

    private listingbrevoTemplate() {
        return new Promise<void>((resolve) => {
            this.sendingblueService
                .getAllTransactionalEmails1(this.tenant)
                .subscribe((res: TemplateSendingblue[]) => {
                    this.brevoTemplates = res;
                    this.brevoTemplate = this.brevoTemplates[0].key;
                    resolve();
                });
        });
    }

    private updatebrevoTemplate(templateId: number) {
        this.brevoTemplate = templateId;
    }

    private loadSenderEmails() {
        return new Promise<void>((resolve) => {
            this.sendingblueService
                .getValidatedSenders(this.tenant)
                .subscribe({
                    next: (res: KeyValueStringString[]) => {
                        this.senderEmails = res.map((sender) => ({
                            label: sender.value,
                            value: sender.key,
                        }));
                        resolve();
                    },
                    error: (error) => {
                        console.error('Erreur lors du chargement des expéditeurs:', error);
                        this.messageService.add({
                            key: 'component',
                            severity: 'warn',
                            summary: this.translate.instant('datastore.warnSummary'),
                            detail: this.translate.instant('marketingEmails.create-edit.messages.senderEmailsLoadErrorDetail'),
                        });
                        resolve();
                    }
                });
        });
    }

    doPopup() {
        this.save(
            () => {
                this.callPopUp();
            },
            () => {
                this.callPopUp();
            }
        );
    }
    private callPopUp() {
        const dmpWidth = 400;
        const sibWidth = screen.width - dmpWidth;
        const sibHeight = screen.height;
        const dmpHeight = screen.height;

        const tenant = this.tenant;
        const partner = this.actual.partner;
        const id = this.actual.id;
        const templateId = this.brevoTemplate;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const windowObjectReference1 = window.open(
            `https://my.brevo.com/camp/template/${templateId}/message-setup/app-smtp`,
            'sib',
            `left=${dmpWidth},top=O,width=${sibWidth},height=${sibHeight}`
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const windowObjectReference2 = window.open(
            `/h/popup/marketing-email/${tenant}/${id}/${templateId}?partner=${partner}`,
            'dmp',
            `left=0,top=0,width=${dmpWidth},height=${dmpHeight}`
        );
    }
}
